import { useEffect, useRef, useState } from "react"
import { motion } from "framer-motion"
import { ArrowLeft, Image as ImageIcon, X, RefreshCw, Camera } from "lucide-react"
import { cn } from "@/lib/utils"
import { GlassButton } from "@/components/ui/GlassButton"
import { GlassSwitch } from "@/components/ui/GlassSwitch"
import { RecordingButton } from "./RecordingButton"
import { useScreenshot } from "@/hooks/useScreenshot"
import { useVoiceRecording } from "@/hooks/useVoiceRecording"
import { transcribeFeedbackAudio } from "@/lib/support/transcribe"
import type { FeedbackCategory, FeedbackTranscriptionSource } from "@/types/index"
import { mensagemParaUsuario } from "@/lib/erroParaUsuario"

const CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  bug: "Algo está com problema",
  idea: "Tenho uma sugestão",
  feature: "Quero uma funcionalidade",
  help: "Não sei usar",
}

const PLACEHOLDERS: Record<FeedbackCategory, string> = {
  bug: "Descreva o que você esperava e o que aconteceu...",
  idea: "Como você gostaria que funcionasse?",
  feature: "Qual problema essa funcionalidade resolveria?",
  help: "O que você está tentando fazer?",
}

interface FeedbackFormProps {
  category: FeedbackCategory
  initialScreenshot: Blob | null
  /** True enquanto a captura de tela em segundo plano (#192) ainda roda. */
  initialScreenshotPending?: boolean
  /** Esconde a captura de tela. Usado pela PÁGINA de Suporte, onde não há
   *  "tela do problema" para capturar — ver a nota no bloco escondido. */
  ocultarCaptura?: boolean
  onBack: () => void
  onSubmit: (args: {
    message: string
    screenshotBlob: Blob | null
    transcriptionSource: FeedbackTranscriptionSource
  }) => void
  isSubmitting: boolean
}

export function FeedbackForm({
  category,
  initialScreenshot,
  initialScreenshotPending = false,
  ocultarCaptura = false,
  onBack,
  onSubmit,
  isSubmitting,
}: FeedbackFormProps) {
  const [message, setMessage] = useState("")
  const [transcriptionSource, setTranscriptionSource] =
    useState<FeedbackTranscriptionSource>("text")
  // Screenshot ON por default — PII masking automático já cobre dados sensíveis
  // (CPF, CNPJ, telefone, email mascarados via lib/support/pii-mask.ts).
  // Usuário pode desligar manualmente se quiser.
  // #192: a captura inicial agora chega em segundo plano (pode estar null no
  // mount e "entrar" depois). Liga o toggle por default quando já há captura
  // ou ela está a caminho.
  const [screenshotEnabled, setScreenshotEnabled] = useState(
    initialScreenshot != null || initialScreenshotPending,
  )
  const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(initialScreenshot)
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(() =>
    initialScreenshot ? URL.createObjectURL(initialScreenshot) : null,
  )
  // Evita re-adotar a captura inicial depois que o usuário já mexeu nela
  // (recapturou ou removeu).
  const initialAdopted = useRef(initialScreenshot != null)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [voiceError, setVoiceError] = useState<string | null>(null)

  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { capture, isCapturing } = useScreenshot()
  const voice = useVoiceRecording()

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = "auto"
    ta.style.height = `${Math.min(ta.scrollHeight, 240)}px`
  }, [message])

  // Cleanup object URL
  useEffect(() => {
    return () => {
      if (screenshotUrl) URL.revokeObjectURL(screenshotUrl)
    }
  }, [screenshotUrl])

  // #192: adota a captura que chega DEPOIS da montagem (rodou em segundo
  // plano ao abrir o widget). Uma vez só, e só se o usuário ainda não mexeu.
  useEffect(() => {
    if (initialScreenshot && !initialAdopted.current) {
      initialAdopted.current = true
      setScreenshotBlob(initialScreenshot)
      setScreenshotUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return URL.createObjectURL(initialScreenshot)
      })
    }
  }, [initialScreenshot])

  const removeScreenshot = () => {
    if (screenshotUrl) URL.revokeObjectURL(screenshotUrl)
    setScreenshotBlob(null)
    setScreenshotUrl(null)
  }

  const retakeScreenshot = async () => {
    removeScreenshot()
    const blob = await capture()
    if (blob) {
      setScreenshotBlob(blob)
      setScreenshotUrl(URL.createObjectURL(blob))
    }
  }

  const handleStartRecording = async () => {
    setVoiceError(null)
    await voice.start()
  }

  const handleStopRecording = async () => {
    const result = await voice.stop()
    if (!result) return
    setIsTranscribing(true)
    try {
      const transcript = await transcribeFeedbackAudio(result.blob)
      setMessage((prev) => (prev ? `${prev.trim()}\n\n${transcript}` : transcript))
      setTranscriptionSource("voice")
    } catch (err) {
      setVoiceError(
        mensagemParaUsuario(err, "Não foi possível transcrever o áudio. Tente gravar de novo ou escreva a mensagem.", "FeedbackForm: transcrição"),
      )
    } finally {
      setIsTranscribing(false)
    }
  }

  const canSubmit = message.trim().length >= 10 && !isSubmitting && !isTranscribing

  return (
    <div className="space-y-4">
      {/* Header com voltar */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="Voltar"
          className={cn(
            "flex items-center justify-center w-7 h-7 rounded-lg",
            "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-glass-hover",
            "transition-colors",
          )}
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <span className="text-sm font-medium text-[var(--text-primary)]">
          {CATEGORY_LABELS[category]}
        </span>
      </div>

      {/* Screenshot preview.
          Fica de fora na PÁGINA de Suporte (`ocultarCaptura`): ali não existe
          "a tela do problema" para capturar — o usuário navegou até o Suporte
          de propósito. No balãozinho flutuante, que abre por cima da tela em
          que o problema aconteceu, a captura continua sendo o que há de mais
          útil. */}
      {!ocultarCaptura && (
      <div className="space-y-2">
        <GlassSwitch
          checked={screenshotEnabled}
          onChange={(next) => {
            setScreenshotEnabled(next)
            if (!next) {
              removeScreenshot()
            } else if (!screenshotBlob && !isCapturing) {
              // #192: em telas pesadas a captura automática é pulada (trava o
              // navegador). Se o usuário ligar o toggle, capturamos sob
              // demanda — ação deliberada, com indicador "Capturando...".
              void retakeScreenshot()
            }
          }}
          label="Incluir captura da tela"
          description="CPF, telefone e email são mascarados automaticamente."
        />

        {screenshotEnabled && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className={cn(
              "relative rounded-lg overflow-hidden border border-glass-border",
              "bg-glass-card",
            )}
            style={{ height: 96 }}
          >
            {isCapturing || (initialScreenshotPending && !screenshotBlob) ? (
              <div className="h-full flex items-center justify-center text-xs text-[var(--text-secondary)] gap-2">
                <Camera className="w-4 h-4 animate-pulse" />
                Capturando...
              </div>
            ) : screenshotUrl ? (
              <>
                <img
                  src={screenshotUrl}
                  alt="Captura da tela"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-1 right-1 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={retakeScreenshot}
                    aria-label="Recapturar"
                    className="w-6 h-6 rounded-md bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition"
                  >
                    <RefreshCw className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={removeScreenshot}
                    aria-label="Remover captura"
                    className="w-6 h-6 rounded-md bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </>
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-[var(--text-secondary)] gap-2">
                <ImageIcon className="w-4 h-4" />
                Sem captura
              </div>
            )}
          </motion.div>
        )}
      </div>
      )}

      {/* Textarea + voz */}
      <div className="space-y-2">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => {
            setMessage(e.target.value)
            if (transcriptionSource === "voice") setTranscriptionSource("text")
          }}
          placeholder={PLACEHOLDERS[category]}
          rows={4}
          maxLength={5000}
          disabled={isSubmitting}
          className={cn(
            "w-full p-3 rounded-xl resize-none text-sm leading-relaxed",
            "backdrop-blur-[16px] bg-glass-card border border-glass-border",
            "text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]",
            "focus:outline-none focus:ring-2 focus:ring-[rgb(var(--accent-blue-rgb)/0.40)] focus:border-[rgb(var(--accent-blue-rgb)/0.40)]",
            "transition-colors",
          )}
        />

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <RecordingButton
              isRecording={voice.isRecording}
              isTranscribing={isTranscribing}
              durationMs={voice.durationMs}
              amplitudes={voice.amplitudes}
              onStart={handleStartRecording}
              onStop={handleStopRecording}
              disabled={!voice.isSupported || isSubmitting}
            />
            {voiceError && (
              <span className="text-[11px] text-red-500">{voiceError}</span>
            )}
            {voice.permissionError && !voice.isRecording && (
              <span className="text-[11px] text-red-500">{voice.permissionError}</span>
            )}
          </div>
          <span className="text-[11px] text-[var(--text-tertiary)]">
            {message.length} / 5000
          </span>
        </div>
      </div>

      {/* Submit */}
      <div className="pt-2">
        <GlassButton
          variant="primary"
          size="md"
          loading={isSubmitting}
          disabled={!canSubmit}
          onClick={() =>
            onSubmit({
              message: message.trim(),
              screenshotBlob: screenshotEnabled ? screenshotBlob : null,
              transcriptionSource,
            })
          }
          className="w-full"
        >
          {isSubmitting ? "Enviando..." : "Enviar para a equipe"}
        </GlassButton>
        {message.trim().length < 10 && message.length > 0 && (
          <p className="text-[11px] text-[var(--text-tertiary)] mt-1.5 text-center">
            Conte um pouco mais — pelo menos 10 caracteres.
          </p>
        )}
      </div>
    </div>
  )
}
